import { useState, useRef, useEffect } from "react"
import { CONVERSATION_NAME_MAX, flattenPastedName } from "./name-limits"
import { X, Users, Camera, Check, Radio } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { acceptAttributeFor, checkFileForUpload, getUploadPolicy } from "@/lib/upload-policy"
import { useDirectorySearch, type DirectoryRow } from "./hooks/useDirectorySearch"
import { DirectoryScopeTabs } from "./directory-scope-tabs"
import { LoadMoreSentinel } from "./load-more-sentinel"
import { toggleSelected } from "./selection-map"
import type { SearchScope } from "@/features/meetings/services/contacts.api"
import { AvatarPhoto } from "./avatar-photo"

interface CreateGroupDialogProps {
  open: boolean
  onClose: () => void
  onCreateGroup: (groupData: { name: string; participants: string[]; imageUrl?: string; imageFile?: File }) => void
  /**
   * `broadcast` reuses this picker for a broadcast LIST: same two-step flow
   * (pick people → name it), different labels, and a 1-person minimum (a list
   * of one is legitimate; a group of one is not).
   */
  variant?: "group" | "broadcast"
}

const scopeLabel: Record<SearchScope, string> = {
  subId: 'sub-Organization',
  scopeId: 'Organization',
  global: 'Global',
}

function Avatar({ name, imageUrl, size = 40 }: { name: string; imageUrl?: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const c = colors[name.charCodeAt(0) % colors.length]
  return (
    <div className={`rounded-full flex items-center justify-center font-semibold shrink-0 overflow-hidden ${c}`} style={{ width: size, height: size }}>
      <AvatarPhoto src={imageUrl} alt={name} className="w-full h-full object-cover" fallback={<span className="text-sm">{initials}</span>} />
    </div>
  )
}

export function CreateGroupDialog({ open, onClose, onCreateGroup, variant = "group" }: CreateGroupDialogProps) {
  const t = useTranslation()
  const isBroadcast = variant === "broadcast"
  // A group may start with just its creator, who adds people later; the server
  // sets only a maximum. A broadcast to nobody would send nothing, so it keeps one.
  const minPeople = isBroadcast ? 1 : 0
  const [step, setStep] = useState<"participants" | "details">("participants")
  // Kept as a Map, not an id array: the directory list this dialog owns now
  // (scope tabs + search + paging) replaces `contacts` wholesale on every
  // scope/query change, so a row picked on one page has to survive being
  // scrolled, searched or tabbed away from. See selection-map.ts.
  const [selectedMap, setSelectedMap] = useState<Map<string, DirectoryRow>>(new Map())
  const [groupName, setGroupName] = useState("")
  const [groupImage, setGroupImage] = useState<string>("")
  const [groupImageFile, setGroupImageFile] = useState<File | undefined>(undefined)
  // Picker filter from the server's policy; any image until it loads.
  const [imageAccept, setImageAccept] = useState("image/*")
  useEffect(() => {
    void getUploadPolicy().then(() => setImageAccept(acceptAttributeFor("image") || "image/*"))
  }, [])
  const [searchQuery, setSearchQuery] = useState("")
  const [scope, setScope] = useState<SearchScope>("global")
  const [nameError, setNameError] = useState("")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const nameFieldRef = useRef<HTMLTextAreaElement>(null)

  const {
    contacts: directoryContacts,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    resultScope,
  } = useDirectorySearch({ enabled: open, scope, query: searchQuery })

  // Grows the name box to fit what's typed instead of scrolling it
  // sideways inside a fixed single line. Resetting to 'auto' first is what
  // lets the box shrink back down after a paste is cut short by maxLength.
  const resizeNameField = () => {
    const el = nameFieldRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }
  useEffect(() => { resizeNameField() }, [groupName, step])

  const toggleParticipant = (row: DirectoryRow) => {
    const alreadySelected = selectedMap.has(row.id)
    setSelectedMap(prev => toggleSelected(prev, row))
    if (!alreadySelected) {
      // Picking someone used to leave the list filtered by their name, so the
      // next contact was hidden until the box was cleared by hand. Clear it here
      // and keep focus in place so typing the next name is immediate.
      setSearchQuery("")
      searchInputRef.current?.focus()
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (file) {
      const check = await checkFileForUpload(file, "image")
      if (!check.ok) {
        toast.error({ title: check.message, description: file.name })
        return
      }
      setGroupImageFile(file)
      const reader = new FileReader()
      reader.onload = () => setGroupImage(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleCreate = () => {
    const trimmed = groupName.trim()
    if (trimmed.length < 2) {
      setNameError(
        isBroadcast
          ? 'Broadcast name must be at least 2 characters'
          : t('presence.groupNameTooShort'),
      )
      return
    }
    if (selectedMap.size < minPeople) {
      setNameError(
        isBroadcast
          ? 'Select at least 1 recipient'
          : t('presence.selectMinParticipants'),
      )
      return
    }
    onCreateGroup({
      name: trimmed,
      participants: Array.from(selectedMap.keys()),
      imageUrl: groupImage || undefined,
      imageFile: groupImageFile
    })
    handleClose()
  }

  // The dialog stays mounted between opens (it only returns null while
  // closed), so its state survives a close. Every way out resets it, so the
  // next New Group starts empty instead of where the last one was abandoned.
  const handleClose = () => {
    setStep("participants")
    setSelectedMap(new Map())
    setGroupName("")
    setGroupImage("")
    setGroupImageFile(undefined)
    setSearchQuery("")
    setScope("global")
    setNameError("")
    onClose()
  }

  const selectedContacts = Array.from(selectedMap.values())

  if (!open) return null

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isBroadcast ? <Radio className="w-5 h-5" /> : <Users className="w-5 h-5" />}
            {step === "participants"
              ? (isBroadcast ? 'Select recipients' : t('presence.selectParticipants'))
              : (isBroadcast ? 'Broadcast details' : t('presence.groupDetails'))}
          </DialogTitle>
        </DialogHeader>

        {step === "participants" ? (
          <div className="space-y-4">
            {/* Search */}
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value.slice(0, 100))}
              placeholder={t('presence.searchContacts')}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/20"
              maxLength={CONVERSATION_NAME_MAX}
            />

            {/* Scope tabs: Global first, same reach as New Chat. */}
            <DirectoryScopeTabs value={scope} onChange={setScope} />

            {/* Selected count */}
            {selectedMap.size > 0 && (
              <div className="text-sm text-muted-foreground">
                {selectedMap.size} {isBroadcast ? 'recipients' : t('common.participants').toLowerCase()} {t('common.selected').toLowerCase()}
              </div>
            )}

            {/* Contacts list: server-searched and ordered; no local filter/sort. */}
            <div className="max-h-80 overflow-y-auto space-y-1">
              {loading && directoryContacts.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-muted-foreground">{t('presence.loading')}</p>
                </div>
              ) : directoryContacts.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-muted-foreground">{t('presence.noContacts')}</p>
                </div>
              ) : (
                <>
                  {resultScope && resultScope !== scope && (
                    <p className="pb-1 text-xs text-muted-foreground">
                      {t('presence.widerScopeNote', { scope: scopeLabel[resultScope] })}
                    </p>
                  )}
                  {directoryContacts.map(row => (
                    <div
                      key={row.id}
                      onClick={() => toggleParticipant(row)}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                    >
                      <Avatar name={row.name} imageUrl={row.imageUrl} size={36} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{row.name}</p>
                        {row.subtitle && (
                          <p className="text-xs text-muted-foreground truncate">{row.subtitle}</p>
                        )}
                        {/* Organisation under the email: one person can be a
                            contact in more than one organisation. The subtitle
                            already is the organisation when there is no email. */}
                        {row.contactOrgName && row.contactOrgName !== row.subtitle && (
                          <p className="text-xs text-muted-foreground truncate">{row.contactOrgName}</p>
                        )}
                      </div>
                      {selectedMap.has(row.id) && (
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                  <LoadMoreSentinel hasMore={hasMore} loading={loadingMore} onVisible={loadMore} />
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">{t('common.cancel')}</Button>
              <Button
                onClick={() => setStep("details")}
                disabled={selectedMap.size < minPeople}
                className="flex-1"
              >
                {t('common.next')} ({selectedMap.size})
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Group image */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                {groupImage ? (
                  <img src={groupImage} alt={t('presence.group')} className="w-20 h-20 rounded-full object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                    {isBroadcast ? <Radio className="w-8 h-8 text-muted-foreground" /> : <Users className="w-8 h-8 text-muted-foreground" />}
                  </div>
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary/90 transition-colors"
                >
                  <Camera className="w-4 h-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={imageAccept}
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              <p className="text-xs text-muted-foreground">{t('presence.tapToAddPhoto')}</p>
            </div>

            {/* Group name. Capped at CONVERSATION_NAME_MAX (100) both on input and
                in onChange, so a pasted name cannot exceed it. A textarea that
                grows with its content, not an input that scrolls it sideways:
                the field needs to stay readable at the full 100 characters. */}
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                {isBroadcast ? 'Broadcast name' : t('presence.groupName')}
              </label>
              <textarea
                ref={nameFieldRef}
                value={groupName}
                onChange={e => {
                  setGroupName(flattenPastedName(e.target.value).slice(0, CONVERSATION_NAME_MAX))
                  if (nameError) setNameError("")
                }}
                onKeyDown={e => { if (e.key === 'Enter') e.preventDefault() }}
                placeholder={isBroadcast ? 'Enter broadcast name' : t('presence.enterGroupName')}
                className={`w-full px-3 py-2 border rounded-lg bg-background text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 resize-none overflow-hidden [overflow-wrap:anywhere] ${nameError ? 'border-destructive focus:ring-destructive/20' : 'border-border focus:ring-primary/20'}`}
                maxLength={CONVERSATION_NAME_MAX}
                rows={1}
              />
              <div className="flex justify-between mt-1">
                {nameError ? <p className="text-xs text-destructive">{nameError}</p> : <span />}
                <span className={`text-xs ${groupName.length >= CONVERSATION_NAME_MAX ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {groupName.length}/{CONVERSATION_NAME_MAX}
                </span>
              </div>
            </div>

            {/* Selected participants preview */}
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                {isBroadcast ? 'Recipients' : t('common.participants')} ({selectedContacts.length})
              </label>
              <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                {selectedContacts.map(contact => (
                  <div key={contact.id} className="flex items-center gap-2 bg-muted rounded-full pl-1 pr-3 py-1">
                    <Avatar name={contact.name} imageUrl={contact.imageUrl} size={24} />
                    <span className="text-xs font-medium">{contact.name}</span>
                    <button
                      onClick={() => toggleParticipant(contact)}
                      className="w-4 h-4 rounded-full bg-muted-foreground/20 hover:bg-destructive/20 flex items-center justify-center transition-colors"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("participants")} className="flex-1">
                {t('common.back')}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!groupName.trim() || selectedMap.size < minPeople}
                className="flex-1"
              >
                {isBroadcast ? 'Create broadcast' : t('presence.createGroup')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
